import { isNil } from "lodash";

import outlineColumnIds from "../../constants/outlineColumnIds";
import SortOptions from "../../constants/sortOptions";
import { itemSelectors } from "../../modules/items";
import dateFunctions from "../definition/calendar/dateFunctions";
import { propertySelectors } from "../definition/properties";
import { typeSelectors } from "../definition/types";

const getReverseSortFunction = sortFn => (a, b) => {
  return -sortFn(a, b);
};

// No sorting, take them as they come!
const bySequence = (a, b) => {
  return a.sequenceIndex - b.sequenceIndex;
};

const byStart = (a, b) => {
  const aN = a.startDate ? a.startDate.timestamp : null;
  const bN = b.startDate ? b.startDate.timestamp : null;

  if (!isNil(aN) && !isNil(bN)) {
    if (aN === bN) {
      if (!a.super && b.super) {
        return -1;
      }
      if (a.super && !b.super) {
        return 1;
      }
      return bySequence(a, b);
    }
    return aN - bN;
  } else if (!isNil(aN)) {
    return -1;
  } else if (!isNil(bN)) {
    return 1;
  } else {
    if (!a.super && b.super) {
      return -1;
    }
    if (a.super && !b.super) {
      return 1;
    }
    return bySequence(a, b);
  }
};

const byEnd = (a, b) => {
  const aN = a.endDate ? a.endDate.timestamp : null;
  const bN = b.endDate ? b.endDate.timestamp : null;

  if (!isNil(aN) && !isNil(bN)) {
    if (aN === bN) {
      if (!a.super && b.super) {
        return -1;
      }
      if (a.super && !b.super) {
        return 1;
      }
      return bySequence(a, b);
    }
    return aN - bN;
  } else if (!isNil(aN)) {
    return -1;
  } else if (!isNil(bN)) {
    return 1;
  } else {
    if (!a.super && b.super) {
      return -1;
    }
    if (a.super && !b.super) {
      return 1;
    }
    return bySequence(a, b);
  }
};

// Depth first
const byNarrative = (a, b) => {
  const aN = a.narrativePosition;
  const bN = b.narrativePosition;
  return narrativePositionSortFunction(aN, bN);
};

const narrativePositionSortFunction = (aN, bN) => {
  if (aN && bN) {
    let n = Math.min(aN.count(), bN.count());
    for (let i = 0; i < n; i++) {
      const ai = aN.get(i);
      const bi = bN.get(i);
      if (ai != bi) {
        return ai - bi;
      }
    }
    return aN.size - bN.size;
  } else if (aN) {
    return -1;
  } else if (bN) {
    return 1;
  } else {
    return 0;
  }
};

const compareStringsOrUseFallback = (a, b, fallbackValue) => {
  const result = a && b ? a.localeCompare(b) : a ? 1 : b ? -1 : 0;
  return result ? result : fallbackValue;
};

const compareNumbersOrUseFallback = (a, b, fallbackValue) => {
  const result = !isNil(a) && !isNil(b) ? a - b : !isNil(a) ? 1 : !isNil(b) ? -1 : 0;
  return result ? result : fallbackValue;
};

const compareBooleansOrUseFallback = (a, b, fallbackValue) => {
  const result = a && !b ? 1 : b && !a ? -1 : 0;
  return result ? result : fallbackValue;
};

const byLabel = (a, b) => {
  return compareStringsOrUseFallback(a.label, b.label, bySequence(a, b));
};

const byObjectLabel = (a, b) => {
  return compareStringsOrUseFallback(a.objectLabel, b.objectLabel);
};

const byDuration = (calendar, a, b) => {
  const aS = dateFunctions.secondsInDuration(a.startDate, a.duration, calendar);
  const bS = dateFunctions.secondsInDuration(b.startDate, b.duration, calendar);
  return !isNil(aS) && !isNil(bS) ? aS - bS : !isNil(aS) ? -1 : !isNil(bS) ? 1 : bySequence(a, b);
};

const createByDuration = calendar => (a, b) => byDuration(calendar, a, b);

const byType = (typesById, a, b) => {
  const aT = typesById.get(a.type).label;
  const bT = typesById.get(b.type).label;
  return compareStringsOrUseFallback(aT, bT, bySequence(a, b));
};

const createByType = typesById => (a, b) => byType(typesById, a, b);

const byColor = (a, b) => {
  const aL = a.colorLabel;
  const bL = b.colorLabel;
  if (!aL && !bL) {
    return compareStringsOrUseFallback(a.color, b.color, bySequence(a, b));
  } else if (!aL) {
    return 1;
  } else if (!bL) {
    return -1;
  }
  return compareStringsOrUseFallback(aL, bL, bySequence(a, b));
};

const byProperty = (prop, a, b) => {
  const aP = a.propertyValues.get(prop.id);
  const bP = b.propertyValues.get(prop.id);

  if (propertySelectors.isNumericProperty(prop)) {
    return compareNumbersOrUseFallback(aP, bP, bySequence(a, b));
  }
  if (propertySelectors.isBooleanProperty(prop)) {
    return compareBooleansOrUseFallback(aP, bP, bySequence(a, b));
  }
  return compareStringsOrUseFallback(aP, bP, bySequence(a, b));
};

const createByProperty = prop => (a, b) => byProperty(prop, a, b);

const byDateThenSequence = (a, b) => {
  const aN = a.startDate ? a.startDate.timestamp : null;
  const bN = b.startDate ? b.startDate.timestamp : null;
  if (!isNil(aN) && !isNil(bN)) {
    if (aN === bN) {
      return a.sequenceIndex - b.sequenceIndex;
    }
    return aN - bN;
  } else if (!isNil(aN)) {
    return -1;
  } else if (!isNil(bN)) {
    return 1;
  } else {
    return a.sequenceIndex - b.sequenceIndex;
  }
};

const byIdentifier = typesById => (a, b) => {
  let result = 0;

  const aIsAvatar = typeSelectors.isAvatarType(typesById.get(a.type));
  const bIsAvatar = typeSelectors.isAvatarType(typesById.get(b.type));
  const aInitials = aIsAvatar ? itemSelectors.getInitialsForItem(a) : a.displayId;
  const bInitials = bIsAvatar ? itemSelectors.getInitialsForItem(b) : b.displayId;

  if (a.type === b.type && !aIsAvatar) {
    const { identifierPrefix } = typesById.get(a.type);
    const aNum = parseInt(a.displayId.replace(identifierPrefix, ""));
    const bNum = parseInt(b.displayId.replace(identifierPrefix, ""));
    result =
      isNaN(aNum) || isNaN(bNum)
        ? compareStringsOrUseFallback(aInitials, bInitials, bySequence(a, b))
        : compareNumbersOrUseFallback(aNum, bNum, bySequence(a, b));
  } else {
    result = compareStringsOrUseFallback(aInitials, bInitials, bySequence(a, b));
  }

  return result;
};

const sortFunctionForOption = option =>
  option === SortOptions.LABEL
    ? byLabel
    : option === SortOptions.NARRATIVE
      ? byNarrative
      : option === SortOptions.SEQUENCE
        ? bySequence
        : option === SortOptions.IDENTIFIER
          ? byIdentifier
          : byStart;

const sortFunctionForColumn = (columnId, calendar, typesById, propertiesById) => {
  switch (columnId) {
    case outlineColumnIds.label:
      return byLabel;
    case outlineColumnIds.narrative:
      return byNarrative;
    case outlineColumnIds.sequence:
      return bySequence;
    case outlineColumnIds.start:
    case outlineColumnIds.latestStart:
      return byStart;
    case outlineColumnIds.earliestEnd:
    case outlineColumnIds.end:
      return byEnd;
    case outlineColumnIds.duration:
      return createByDuration(calendar);
    case outlineColumnIds.type:
      return createByType(typesById);
    case outlineColumnIds.color:
      return byColor;
    case outlineColumnIds.identifier:
      return byIdentifier(typesById);
    default:
      break;
  }

  // If we reach here, maybe it is a property instead?
  if (propertiesById.has(columnId)) {
    return createByProperty(propertiesById.get(columnId));
  }

  // Fallback
  return bySequence;
};

export { getReverseSortFunction, narrativePositionSortFunction };

export default {
  sortFunctionForOption,
  sortFunctionForColumn,
  byStart,
  byLabel,
  byNarrative,
  bySequence,
  byDateThenSequence,
  byObjectLabel,
  createByDuration
};
